/**
 * "Madness" — how Pixel feels about the CONTACT button.
 *
 *  excited   First couple of hovers: a soft, happy wiggle.
 *  pissed    Stage 1 — keep hovering without clicking: an annoyed face that sharpens the closer you get.
 *  mad       Stage 1½ — a scripted glare: hard head snaps button ↔ you, a narrow-eyed squint, then he
 *            moves right up to the glass (blocking everything but CONTACT), tracks your cursor and
 *            jerks his head at the button. Click now and all is forgiven.
 *  psychic   Stage 2 — ignored: he backs off deep into the scene and crashes into the screen.
 *            The glass cracks, then fades away after ~13s.
 *  dizzy     ~20s of nausea afterwards: wobbling, swaying, eyes rolling.
 *
 * A tiny global store: the nav writes to it, the 3D scene reads it every frame,
 * and the glass overlay subscribes to the cracks.
 */
export type MadnessStage = 'calm' | 'excited' | 'pissed' | 'mad' | 'psychic' | 'dizzy';

export interface MadnessSample {
  stage: MadnessStage;
  /** 0 (far) .. 1 (cursor on CONTACT) */
  proximity: number;
  /** Seconds into the mad glare sequence, or -1 */
  madT: number;
  /** Seconds since the crash started, or -1 */
  crashT: number;
  /** Seconds into the post-crash dizziness, or -1 */
  dizzyT: number;
  /** CONTACT button centre in -1..1 screen space (y up) */
  contact: { x: number; y: number };
}

export interface GlassCrackMark {
  x: number;
  y: number;
  seed: number;
}

/** Counted hovers before he's pissed (stage 1) — the first four just make him happy */
const PISSED_AT = 5;
/** …and before the mad glare starts */
const MAD_AT = 7;
/** Minimum time between counted hovers (ms) — just enough to ignore the cursor jittering on the button's
 *  edge, so quick back-and-forth teasing counts straight away */
const HOVER_GAP = 150;
/** Lingering on CONTACT this long while pissed also sets him off (ms) */
const MAD_HOLD = 1800;
/** He calms down after this long without being teased (ms) */
const COOL_DOWN = 20000;
/** Excitement lingers this long after the cursor leaves (ms) */
const EXCITE_LINGER = 1200;
/** Happy wiggle after you finally click (ms) */
const CLICK_JOY = 1600;
/** Glare sequence length (s) — not clicked by then → crash */
export const MAD_DUR = 13.8;
/** Nausea / dizziness after the crash (s) */
export const DIZZY_DUR = 20;
/** Crash sequence length (s) and the moment he hits the glass */
export const CRASH_DUR = 3.6;
export const CRASH_IMPACT = 1.85;
/** Cracks kept on screen at once */
const MAX_CRACKS = 3;

class MadnessStore {
  private hovers = 0;
  private hovering = false;
  private enterAt = 0;
  private leaveAt = -1e9;
  private lastTeaseAt = -1e9;
  private lastCountedAt = -1e9;
  private clickedAt = -1e9;
  private madAt = -1;
  private crashAt = -1;
  private dizzyAt = -1;
  private impactDone = false;
  private proximity = 0;
  private contact = { x: 0.85, y: 0.9 };
  private cracks: GlassCrackMark[] = [];
  private listeners = new Set<() => void>();

  enter = () => {
    const now = performance.now();
    if (now - this.lastTeaseAt > COOL_DOWN && this.madAt < 0) this.hovers = 0;
    this.hovering = true;
    this.enterAt = now;
    this.lastTeaseAt = now;
    if (this.crashAt >= 0 || this.madAt >= 0) return;
    // Still seeing stars — teasing doesn't count yet
    if (this.dizzyAt >= 0 && now - this.dizzyAt < DIZZY_DUR * 1000) return;
    if (now - this.lastCountedAt < HOVER_GAP) return;
    this.lastCountedAt = now;
    this.hovers++;
    if (this.hovers >= MAD_AT) this.madAt = now;
  };

  leave = () => {
    this.hovering = false;
    this.leaveAt = performance.now();
  };

  /** Clicking CONTACT forgives everything — unless he's already mid-crash */
  click = () => {
    if (this.crashAt >= 0) return;
    this.hovers = 0;
    this.madAt = -1;
    this.clickedAt = performance.now();
  };

  setProximity = (p: number) => {
    this.proximity = Math.min(1, Math.max(0, p));
  };

  setContact = (x: number, y: number) => {
    this.contact = { x, y };
  };

  /** Called by the scene the moment Pixel hits the glass (screen px) */
  impact = (x: number, y: number) => {
    if (this.crashAt < 0 || this.impactDone) return;
    this.impactDone = true;
    this.cracks = [...this.cracks, { x, y, seed: Math.floor(Math.random() * 1e9) }].slice(-MAX_CRACKS);
    this.emit();
  };

  removeCrack = (seed: number) => {
    this.cracks = this.cracks.filter((c) => c.seed !== seed);
    this.emit();
  };

  needsImpact = (crashT: number) => this.crashAt >= 0 && !this.impactDone && crashT >= CRASH_IMPACT;

  getCracks = () => this.cracks;

  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  sample(now: number): MadnessSample {
    let crashT = -1;
    let madT = -1;
    if (this.crashAt >= 0) {
      crashT = (now - this.crashAt) / 1000;
      if (crashT >= CRASH_DUR) {
        this.crashAt = -1;
        this.hovers = 0;
        crashT = -1;
        this.dizzyAt = now; // seeing stars for a while
      }
    } else if (this.madAt >= 0) {
      madT = (now - this.madAt) / 1000;
      if (madT >= MAD_DUR) {
        // Ignored — here he comes
        this.madAt = -1;
        madT = -1;
        this.crashAt = now;
        this.impactDone = false;
        crashT = 0;
      }
    } else if (this.hovering && this.hovers >= PISSED_AT && now - this.enterAt > MAD_HOLD) {
      this.madAt = now;
      madT = 0;
    } else if (!this.hovering && this.hovers > 0 && now - this.lastTeaseAt > COOL_DOWN) {
      this.hovers = 0;
    }

    let dizzyT = -1;
    if (this.dizzyAt >= 0 && crashT < 0) {
      dizzyT = (now - this.dizzyAt) / 1000;
      if (dizzyT >= DIZZY_DUR) {
        this.dizzyAt = -1;
        dizzyT = -1;
      }
    }

    let stage: MadnessStage = 'calm';
    if (crashT >= 0) stage = 'psychic';
    else if (madT >= 0) stage = 'mad';
    else if (dizzyT >= 0) stage = 'dizzy';
    else if (now - this.clickedAt < CLICK_JOY) stage = 'excited';
    else if (this.hovers >= PISSED_AT) stage = 'pissed';
    else if (this.hovers > 0 && (this.hovering || now - this.leaveAt < EXCITE_LINGER)) stage = 'excited';

    return { stage, proximity: this.proximity, madT, crashT, dizzyT, contact: this.contact };
  }

  private emit() {
    this.listeners.forEach((l) => l());
  }
}

export const madness = new MadnessStore();
